import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import PageTitle from "../common/PageTitle";
import BillLogo from "./shared/BillLogo";
import { Button, Grid, TextField, Typography, Autocomplete, CircularProgress, Box, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useMutation } from "react-query";
import {
  nedcoBuyVoucherApiCaller,
  nedcoEnquiryApiCaller,
} from "shared/endPoints/apiCallers";
import Notiflix from "notiflix";
import { intlTranselate } from "shared/utils/ppUtils";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import translate from "i18n/translate";
import ShareModal from "components/profile/ShareModal";
import ApiRequest from "../../actions/ApiRequest";
import LoadingButton from "@mui/lab/LoadingButton";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

const propTypes = {};

const translations = {
  ar: {
    account_number: "رقم الحساب",
    desired_amount: "المبلغ",
    electric_company: "شركة الكهرباء",
    meter_number: "رقم المشترك",
    name: "اسم المشترك ",
    tarif_description: "نوع الحساب",
    less_than_21: "على الاقل 21 شيقل",
    value: "القيمه",
    search: "بحث",
    all_customers: "بحث موسّع",
    "searching...": "جاري البحث ...",
    Error: "خطأ",
    "Returned MSG": "رسالة الخطأ",
    "Amount Due": "المبلغ المستحق",
    "Remaining Amount": "المبلغ المتبقي",
    voucher_number: "رقم الفاتورة",
    no_of_kw: "عدد الكيلوات",
    Confirm: "تأكيد",
    Close: "إغلاق",
    Share: "مشاركة",
    Print: "طباعة",
    Back: "رجوع",
    confirm_prompt: "هل أنت متأكد؟",
    fees: "الرسوم",
    amount_due: "المبلغ المستحق",
    remaining_amount: "المبلغ المتبقي",
    fees_details: "تفاصيل الرسوم",
    service_fee: "رسوم الخدمة",
    reference: "المرجع",
    fees_note: "ملاحظة عن الرسوم",
    trans_id: "رقم العملية",
    date: "التاريخ",
    SmallPrint: "طباعة (POS)",
  },
  en: {
    account_number: "Account Number",
    desired_amount: "Desired Amount",
    electric_company: "Electric Company",
    meter_number: "Meter Number",
    name: "Name",
    tarif_description: "Tarif Description",
    less_than_21: "At least 21 NIS",
    value: "Value",
    search: "Search",
    all_customers: "Extended Search",
    "searching...": "Searching ...",
    Error: "Error",
    "Returned MSG": "Returned MSG",
    "Amount Due": "Amount Due",
    "Remaining Amount": "Remaining Amount",
    voucher_number: "Voucher Number",
    no_of_kw: "No of KW",
    Confirm: "Confirm",
    Close: "Close",
    Share: "Share",
    Print: "Print",
    Back: "Back",
    confirm_prompt: "Are you sure?",
    fees: "Fees",
    amount_due: "Amount Due",
    remaining_amount: "Remaining Amount",
    fees_details: "Fee Details",
    service_fee: "Service Fee",
    reference: "Reference",
    fees_note: "Fee Note",
    trans_id: "Transaction ID",
    date: "Date",
    SmallPrint: "Small Print (POS)",
  },
  he: {
    account_number: "מספר חשבון",
    desired_amount: "כמות מבוקשת",
    electric_company: "חברת חשמל",
    meter_number: "מספר מונה",
    name: "שם",
    tarif_description: "תיאור תעריף",
    less_than_21: "לפחות 21 שקל",
    value: "ערך",
    search: "חיפוש",
    all_customers: "חיפוש מורחב",
    "searching...": "מחפש ...",
    "Remaining Amount": "הסכום הנותר",
    "Amount Due": "סכום לתשלום",
    "Returned MSG": "הודעה שחזרה",
    Error: "שגיאה",
    voucher_number: "Voucher Number",
    no_of_kw: "מספר קו",
    Confirm: "אשר",
    Close: "סגור",
    Share: "שתף",
    Print: "הדפס",
    Back: "חזרה",
    confirm_prompt: "האם אתה בטוח?",
    fees: "עמלות",
    amount_due: "סכום לתשלום",
    remaining_amount: "הסכום הנותר",
    fees_details: "פרטי עמלות",
    service_fee: "דמי שירות",
    reference: "אסמכתא",
    fees_note: "הערה על עמלות",
    trans_id: "מזהה עסקה",
    date: "תאריך",
    SmallPrint: "הדפסה קטנה (POS)",
  },
};

const normalizeFieldLabel = (label = "") =>
  label.toString().trim().replace(/[:：]/g, "").toLowerCase();

const FIELD_ALIASES = [
  {
    key: "name",
    aliases: [
      "name",
      "اسم",
      "اسم المشترك",
      "اسم العميل",
      "الاسم",
      "اسم المشترك",
    ],
  },
  {
    key: "account_number",
    aliases: [
      "account number",
      "account_number",
      "accountno",
      "account no",
      "رقم الحساب",
      "رقم الحساب",
    ],
  },
  {
    key: "meter_number",
    aliases: [
      "meter number",
      "meter_number",
      "meter",
      "رقم العداد",
      "رقم المشترك",
      "رقم الاشتراك",
    ],
  },
  {
    key: "electric_company",
    aliases: [
      "electric company",
      "electric_company",
      "شركة الكهرباء",
      "الشركة",
    ],
  },
  {
    key: "desired_amount",
    aliases: [
      "desired amount",
      "desired_amount",
      "requested amount",
      "المبلغ المطلوب",
      "المبلغ",
      "amount",
    ],
  },
  {
    key: "value",
    aliases: ["value", "القيمه", "القيمة"],
  },
  {
    key: "amount_due",
    aliases: [
      "amount due",
      "amount_due",
      "due amount",
      "المبلغ المستحق",
    ],
  },
  {
    key: "remaining_amount",
    aliases: [
      "remaining amount",
      "remaining_amount",
      "balance",
      "المبلغ المتبقي",
      "الرصيد المتبقي",
    ],
  },
  {
    key: "fees",
    aliases: ["fees", "total fees", "الرسوم"],
  },
  {
    key: "fees_details",
    aliases: ["fees details", "fee details", "تفاصيل الرسوم"],
  },
  {
    key: "fees_note",
    aliases: ["fee note", "note on fees", "ملاحظة عن الرسوم"],
  },
  {
    key: "service_fee",
    aliases: ["service fee", "service fees", "رسوم الخدمة"],
  },
  {
    key: "reference",
    aliases: ["reference", "reference number", "ref", "المرجع"],
  },
  {
    key: "tarif_description",
    aliases: [
      "tarif description",
      "tariff description",
      "نوع الحساب",
      "نوع التعرفة",
    ],
  },
  {
    key: "voucher_number",
    aliases: [
      "voucher number",
      "voucher_number",
      "رقم الفاتورة",
      "رقم القسيمة",
      "voucher",
    ],
  },
  {
    key: "no_of_kw",
    aliases: [
      "no of kw",
      "no_of_kw",
      "kilowatt",
      "عدد الكيلوات",
      "عدد الكيلو وات",
    ],
  },
  {
    key: "trans_id",
    aliases: [
      "trans_id",
      "transaction id",
      "transid",
      "رقم العملية",
      "מזהה עסקה",
    ],
  },
  {
    key: "date",
    aliases: [
      "date",
      "transaction date",
      "التاريخ",
      "תאריך",
    ],
  },
];

const ALIAS_TO_CANONICAL_KEY = FIELD_ALIASES.reduce((acc, { key, aliases }) => {
  aliases.forEach((alias) => {
    acc[normalizeFieldLabel(alias)] = key;
  });
  return acc;
}, {});

const findCanonicalKey = (rawKey) => {
  if (!rawKey) return null;
  const normalized = normalizeFieldLabel(rawKey);
  if (ALIAS_TO_CANONICAL_KEY[normalized]) {
    return ALIAS_TO_CANONICAL_KEY[normalized];
  }

  for (const { key, aliases } of FIELD_ALIASES) {
    for (const alias of aliases) {
      const aliasNormalized = normalizeFieldLabel(alias);
      if (
        aliasNormalized &&
        (normalized.includes(aliasNormalized) ||
          aliasNormalized.includes(normalized))
      ) {
        return key;
      }
    }
  }

  return null;
};

const getLocalizedFieldLabel = (rawKey, localeStrings) => {
  if (!rawKey) return "";
  const canonicalKey = findCanonicalKey(rawKey);

  const fallback =
    (canonicalKey && localeStrings?.[canonicalKey]) ||
    (canonicalKey && translations.en?.[canonicalKey]) ||
    localeStrings?.[rawKey] ||
    translations.en?.[rawKey] ||
    rawKey;

  return typeof fallback === "string"
    ? fallback.replace(/[:：]+$/, "").trim()
    : fallback;
};

export default function Nedco(props) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({ amount: 0, sNumber: "" });
  const [userInformation, setUserInformation] = useState({});
  const [amountIsValid, setAmountIsValid] = useState(true);
  const [printedData, setPrintedData] = useState({});
  const [reviewData, setReviewData] = useState({});
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [showOthersButtons, setShowOthersButtons] = useState(false);
  const [isLoading1, setIsLoading1] = useState(false);
  const [nedcoBuyData, setNedcoBuyData] = useState(null);

  const sanitizePrintableEntries = useCallback((entries = []) => {
    if (!Array.isArray(entries)) return entries;

    const sanitized = entries.filter(([key]) => {
      const normalizedKey = String(key || "").toLowerCase();

      return !(
        normalizedKey.includes("voucher_number") ||
        normalizedKey.includes("fee note") ||
        normalizedKey.includes("fees_note") ||
        normalizedKey.includes("service fee")
      );
    });

    return sanitized;
  }, []);
  const [isError, setIsError] = useState(false);
  const [customersList, setCustomersList] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // "ADV" mode: browse ALL Nedco customers across every dealer (no seller filter)
  const [allCustomersList, setAllCustomersList] = useState([]);
  const [loadingAllCustomers, setLoadingAllCustomers] = useState(false);
  const [advMode, setAdvMode] = useState(false);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const rawLocale = localStorage.langCity;
  const locale = ["ar", "en", "he"].includes(rawLocale) ? rawLocale : "en";
  const localeStrings = translations[locale];
  const isRTL = locale === "ar" || locale === "he";
  const history = useHistory();
  const sellerId = useSelector((state) => state.auth.user?.id);

  useEffect(() => {
    const { state } = history.location;

    if (state?.number) {
      setFormData({ amount: 0, sNumber: state.number });
    }
  }, []);

  // Listen for Top20 number click events
  useEffect(() => {
    const handleFillPhoneNumber = (event) => {
      const phoneNumber = event.detail?.phoneNumber;
      if (phoneNumber) {
        console.log('Nedco: Received phone number from Top20:', phoneNumber);
        setFormData((prev) => ({ ...prev, sNumber: phoneNumber }));
        setSelectedCustomer(null); // Clear autocomplete selection
      }
    };

    window.addEventListener('fillBillPhoneNumber', handleFillPhoneNumber);

    return () => {
      window.removeEventListener('fillBillPhoneNumber', handleFillPhoneNumber);
    };
  }, []);

  // Fetch customer list from API (optional feature - gracefully degrades to manual entry)
  useEffect(() => {
    const fetchCustomers = async () => {
      if (!sellerId) {
        setCustomersList([]);
        return;
      }

      try {
        setLoadingCustomers(true);
        const response = await ApiRequest.get(
          `electric_customer_search?seller_id=${sellerId}&provider=nedco`
        );

        if (response?.data?.status === 'success' && response?.data?.customers && Array.isArray(response.data.customers)) {
          const customers = response.data.customers;

          // Filter out invalid entries (N/A names)
          const validCustomers = customers.filter(customer => {
            const name = (customer["اسم العميل"] || '').trim();
            return name && name !== 'N/A';
          });

          if (validCustomers.length > 0) {
            setCustomersList(validCustomers);
            console.log(`Successfully loaded ${validCustomers.length} Nedco customers`);
          } else {
            setCustomersList([]);
          }
        } else {
          // API returned but no customers or failed status
          setCustomersList([]);
        }
      } catch (error) {
        // Silently fail - endpoint might not exist yet or network error
        // User can still use manual entry
        console.log('Customer search unavailable:', error.message);
        setCustomersList([]);
      } finally {
        setLoadingCustomers(false);
      }
    };

    fetchCustomers();
  }, [sellerId]);

  // Fetch ALL Nedco customers across every dealer (no seller filter) for the
  // "ADV" button. Deduplicated server-side by meter number.
  const fetchAllNedcoCustomers = async () => {
    try {
      setLoadingAllCustomers(true);
      const response = await ApiRequest.get(
        `electric_customer_search?all_dealers=true&company_name=Nedco`
      );

      if (
        response?.data?.status === "success" &&
        Array.isArray(response?.data?.customers)
      ) {
        const validCustomers = response.data.customers.filter((customer) => {
          const name = (
            customer["اسم العميل"] ||
            customer.customer_name ||
            customer.name ||
            ""
          ).trim();
          return name && name !== "N/A";
        });
        setAllCustomersList(validCustomers);
        return validCustomers;
      }

      setAllCustomersList([]);
      return [];
    } catch (error) {
      console.log("Adv customer search unavailable:", error.message);
      setAllCustomersList([]);
      return [];
    } finally {
      setLoadingAllCustomers(false);
    }
  };

  // ES button: toggles Extended Search on the same button.
  //  - OFF -> ON: switch to all-dealers list, clear the input so the full list
  //    shows unfiltered, then open the dropdown.
  //  - ON -> OFF: revert to this dealer's own customers and close the dropdown.
  const handleAdvClick = async () => {
    if (advMode) {
      // Disable Extended Search → back to this dealer's customers
      setAdvMode(false);
      setSelectedCustomer(null);
      setFormData((prev) => ({ ...prev, sNumber: "" }));
      setAutocompleteOpen(false);
      return;
    }
    // Enable Extended Search → all dealers' customers
    setAdvMode(true);
    setSelectedCustomer(null);
    setFormData((prev) => ({ ...prev, sNumber: "" }));
    await fetchAllNedcoCustomers();
    setAutocompleteOpen(true);
  };

  const {
    mutateAsync: nedcoEnquiry,
    isLoading,
    data,
  } = useMutation(nedcoEnquiryApiCaller, {
    onSuccess: (res) => {
      if (res?.status === "failed") {
        Notiflix.Notify.failure(res?.reason || "Something went wrong");
        return;
      }
      const dataResponse = res?.data;
      // console.log("dataResponse", dataResponse);
      if ("Error" in dataResponse) {
        setIsError(true);
      } else {
        setIsError(false);
      }
      const sortedData = {};
      Object.keys(dataResponse)
        .sort()
        .forEach((key) => {
          const localizedLabel = getLocalizedFieldLabel(key, localeStrings);
          sortedData[localizedLabel] = dataResponse[key];
        });
      setUserInformation(sortedData);
      setShowConfirm(true);
    },

    onError: (error) => {
      console.log(error);
      Notiflix.Notify.failure(`Something went wrong ! `, "", "Close");
    },
  });

  // const {
  //   mutateAsync: callBuyVoucher,
  //   isLoading: isBuying,
  //   data: nedcoBuyData,
  // } = useMutation(nedcoBuyVoucherApiCaller, {
  //   onSuccess: (res) => {
  //     if (res?.status === "failed") {
  //       Notiflix.Notify.failure(res?.reason || "Something went wrong");
  //       return;
  //     }
  //     console.log("res", res);
  //     Notiflix.Notify.success(res?.reason || "Ssuccess");
  //     const mergedBasicData = Object.assign({}, ...res.data.Basic_data);
  //     const mergedExtraData = Object.assign({}, ...res.data.extra_data);

  //     const combinedObject = { ...mergedBasicData, ...mergedExtraData };

  //     setPrintedData(combinedObject);
  //     setReviewData(mergedBasicData);
  //     setShowOthersButtons(true);
  //   },
  //   onError: () => {
  //     Notiflix.Notify.failure(`Something went wrong !`, "", "Close");
  //   },
  // });
  const onSubmit = (formData) => {
    setShowConfirm(false);
  };
  const callBuyVoucherApi = async (form) => {
    setIsLoading1(true);
    try {
      const res = await ApiRequest.post(
        `/nedco_buy_voucher?s_number=${form.sNumber}&amount=${form.amount}`
      );
      if (res?.status === "failed") {
        Notiflix.Notify.failure(res?.reason || "Something went wrong");
        return;
      }
      const basicData = res.data.Basic_data;
      const extraData = res.data.extra_data;
      const mergedBasicData = Object.assign({}, basicData);
      const mergedExtraData = Object.assign({}, extraData);

      const updatedData = {
        ...userInformation,
      };
      const voucherNumber = enforceLTR(
        mergedExtraData["voucher_number"]
      ).replace(/\*/g, "");
      updatedData[getLocalizedFieldLabel("voucher_number", localeStrings)] =
        voucherNumber;
      updatedData[getLocalizedFieldLabel("no_of_kw", localeStrings)] =
        mergedExtraData["no_of_kw"];

      setNedcoBuyData(updatedData);

      const combinedObject = { ...mergedBasicData, ...mergedExtraData };
      combinedObject["voucher_number"] = voucherNumber;

      console.log("Nedco Debug - combinedObject keys:", Object.keys(combinedObject));

      const targetKeys = [
        "electric_company",
        "meter_number",
        "name",
        "desired_amount",
        "remaining_amount",
        "tarif_description",
        "no_of_kw",
        "voucher_number",
        "trans_id",
        "date"
      ];

      const stringifiedData =
        "\n" +
        targetKeys.map(targetKey => {
          const entry = Object.entries(combinedObject).find(([k, v]) => {
            const canonical = findCanonicalKey(k) || k;
            return canonical === targetKey;
          });

          if (!entry) return null;

          const [key, value] = entry;
          const label = getLocalizedFieldLabel(key, localeStrings);

          if (targetKey === "voucher_number") {
            return `*${label}: ${value}*`;
          }
          return `${label}: ${value}`;
        }).filter(Boolean).join("\n") +
        "\n";

      console.log("Nedco Debug - stringifiedData:", stringifiedData);

      setReviewData(stringifiedData);
      setPrintedData(combinedObject);
      setShowOthersButtons(true);
    } catch (e) {
      console.log(e);
      Notiflix.Notify.failure(`Something went wrong !`, "", "Close");
    } finally {
      setIsLoading1(false);
    }
  };

  function printData(obj) {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    const voucherNumber = obj["voucher_number"]
      ? enforceLTR(obj["voucher_number"]).replace(/\*/g, "")
      : 'N/A';

    // Create modern receipt HTML
    const entriesForPrinting = sanitizePrintableEntries(
      Object.entries(obj || {})
    );

    const printableContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Nedco Voucher Receipt</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
            
            * { margin: 0; padding: 0; box-sizing: border-box; }
            
            body { 
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
              background: linear-gradient(135deg, #00b894 0%, #00cec9 100%);
              padding: 20px;
              min-height: 100vh;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            
            .receipt-container {
              background: white;
              border-radius: 20px;
              box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
              padding: 0;
              max-width: 450px;
              width: 100%;
              overflow: hidden;
              position: relative;
            }
            
            .receipt-header {
              background: linear-gradient(135deg, #00b894 0%, #00cec9 100%);
              color: white;
              padding: 30px 25px;
              text-align: center;
              position: relative;
            }
            
            .nedco-logo {
              width: 100px;
              height: 100px;
              background: white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 auto 15px;
              padding: 10px;
              box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            }
            
            .nedco-logo img {
              width: 100%;
              height: 100%;
              object-fit: contain;
              border-radius: 50%;
            }
            
            .receipt-title {
              font-size: 24px;
              font-weight: 700;
              margin-bottom: 8px;
              text-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            
            .receipt-subtitle {
              font-size: 14px;
              opacity: 0.9;
              font-weight: 400;
            }
            
            .receipt-body {
              padding: 30px 25px;
            }
            
            .status-badge {
              display: inline-block;
              background: #00b894;
              color: white;
              padding: 8px 16px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: 25px;
            }
            
            .voucher-number-highlight {
              background: linear-gradient(135deg, #fdcb6e 0%, #e17055 100%);
              color: white;
              border-radius: 15px;
              padding: 25px;
              text-align: center;
              margin: 25px 0;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }
            
            .voucher-label {
              font-size: 12px;
              opacity: 0.9;
              margin-bottom: 8px;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            
            .voucher-code {
              font-size: 32px;
              font-weight: 900;
              font-family: 'Courier New', monospace;
              letter-spacing: 2px;
              word-break: break-all;
            }
            
            .info-grid {
              display: grid;
              gap: 15px;
              margin-bottom: 20px;
            }
            
            .info-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 12px 0;
              border-bottom: 1px solid #f0f0f0;
            }
            
            .info-item:last-child {
              border-bottom: none;
            }
            
            .info-label {
              font-size: 13px;
              color: #666;
              font-weight: 500;
            }
            
            .info-value {
              font-size: 13px;
              color: #333;
              font-weight: 600;
              text-align: right;
            }
            
            .decorative-line {
              height: 4px;
              background: linear-gradient(90deg, #00b894, #00cec9, #00b894);
              margin: 20px 0;
              border-radius: 2px;
            }
            
            .receipt-footer {
              background: #f8f9fa;
              padding: 20px 25px;
              text-align: center;
              border-top: 1px solid #e0e0e0;
            }
            
            .footer-text {
              font-size: 12px;
              color: #666;
              line-height: 1.5;
            }
            
            .footer-logo {
              width: 60px;
              height: 60px;
              margin: 10px auto 0;
              opacity: 0.3;
            }
            
            @media print {
              @page {
                size: A4;
                margin: 10mm;
              }
              
              body { 
                background: white; 
                padding: 0; 
                min-height: auto;
                margin: 0;
              }
              
              .receipt-container { 
                box-shadow: none; 
                border: 1px solid #ddd; 
                max-width: 100%;
                page-break-inside: avoid;
                margin: 0;
              }
              
              /* Browsers strip background colors when printing, which left
                 the header/voucher text white-on-white, and the gray labels
                 print as faint dithered ink. Force solid black on white. */
              .receipt-header {
                padding: 15px 20px;
                background: #fff !important;
                color: #000 !important;
                border-bottom: 2px solid #000;
              }

              .nedco-logo {
                width: 60px;
                height: 60px;
                margin: 0 auto 10px;
                box-shadow: none;
              }

              .receipt-title {
                font-size: 18px;
                text-shadow: none;
              }

              .receipt-subtitle {
                font-size: 12px;
                opacity: 1;
                font-weight: 600;
              }

              .receipt-body {
                padding: 15px 20px;
              }

              .status-badge {
                background: #fff !important;
                color: #000 !important;
                border: 2px solid #000;
              }

              .voucher-number-highlight {
                padding: 15px;
                margin: 15px 0;
                background: #fff !important;
                color: #000 !important;
                border: 2px solid #000;
                box-shadow: none;
              }

              .voucher-label {
                opacity: 1;
                font-weight: 700;
              }

              .voucher-code {
                font-size: 24px;
                font-weight: 900;
              }

              .info-item {
                padding: 8px 0;
                border-bottom: 1px solid #999;
              }

              .info-label {
                color: #000;
                font-weight: 600;
              }

              .info-value {
                color: #000;
                font-weight: 700;
              }

              .decorative-line {
                background: #000 !important;
              }

              .receipt-footer {
                padding: 12px 20px;
                background: #fff !important;
              }

              .footer-text {
                color: #000;
              }
            }
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <div class="receipt-header">
              <div class="nedco-logo">
                <img src="https://images.celltop.vip/logo_location/nedco.jpg" alt="Nedco Logo" />
              </div>
              <div class="receipt-title">Electricity Voucher</div>
              <div class="receipt-subtitle">Nedco Payment Receipt</div>
            </div>
            
            <div class="receipt-body">
              <div class="status-badge">✓ Voucher Generated</div>
              
              <div class="voucher-number-highlight">
                <div class="voucher-label">Voucher Code</div>
                <div class="voucher-code">${voucherNumber}</div>
              </div>
              
              <div class="decorative-line"></div>
              
              <div class="info-grid">
                ${entriesForPrinting
        .map(([key, value]) => `
                    <div class="info-item">
                      <span class="info-label">${getLocalizedFieldLabel(key, localeStrings)}</span>
                      <span class="info-value">${value}</span>
                    </div>
                  `).join('')}
              </div>
            </div>
            
            <div class="receipt-footer">
              <div class="footer-text">
                Thank you for using Nedco services<br>
                Please keep this voucher safe<br>
                <strong>Printed on: ${formattedDate}</strong>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;

    // Use a new window to open the printable content
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(printableContent);
    printWindow.document.close();
    printWindow.print();
  }

  function printPOSData(obj) {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleString();

    const voucherNumber = obj["voucher_number"]
      ? enforceLTR(obj["voucher_number"]).replace(/\*/g, "")
      : "N/A";

    const getValue = (keys) => {
      for (const key of keys) {
        if (obj[key] !== undefined && obj[key] !== null) return obj[key];
        const localized = getLocalizedFieldLabel(key, localeStrings);
        if (obj[localized] !== undefined && obj[localized] !== null) return obj[localized];
      }
      return "N/A";
    };

    const customerName = getValue(["name", "Name", "customer_name", "Customer Name"]);
    const amount = getValue(["desired_amount", "Desired Amount", "amount", "Amount"]);
    const noOfKw = getValue(["no_of_kw", "No of KW", "no_of_kw", "No Of KW"]);

    const printableContent = `
      <!DOCTYPE html>
      <html dir="${isRTL ? "rtl" : "ltr"}">
        <head>
          <meta charset="UTF-8">
          <title>POS Receipt</title>
          <style>
            @page { margin: 0; }
            /* Thermal printers render thin/gray glyphs very faintly - keep
               everything pure black, bold and sans-serif for max ink density. */
            * {
              color: #000 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            body {
              font-family: Arial, Helvetica, sans-serif;
              width: 58mm;
              margin: 0;
              padding: 2mm;
              font-size: 13px;
              font-weight: 700;
              color: #000;
              line-height: 1.3;
            }
            .header { text-align: center; font-weight: 900; margin-bottom: 5px; border-bottom: 2px dashed #000; padding-bottom: 3px; font-size: 16px; }
            .item { display: flex; justify-content: space-between; margin-bottom: 3px; }
            .label { font-weight: 900; }
            .voucher-box { border: 2px solid #000; padding: 6px; text-align: center; margin: 6px 0; font-size: 20px; font-weight: 900; letter-spacing: 1px; word-break: break-all; }
            .footer { text-align: center; margin-top: 5px; font-size: 11px; font-weight: 700; border-top: 2px dashed #000; padding-top: 3px; }
            @media print {
              body { width: 58mm; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            NEDCO
          </div>
          <div class="info">
            <div class="item">
              <span class="label">${getLocalizedFieldLabel("name", localeStrings)}:</span>
              <span>${customerName}</span>
            </div>
            <div class="item">
              <span class="label">${getLocalizedFieldLabel("desired_amount", localeStrings)}:</span>
              <span>${amount}</span>
            </div>
            <div class="item">
              <span class="label">${getLocalizedFieldLabel("no_of_kw", localeStrings)}:</span>
              <span>${noOfKw}</span>
            </div>
          </div>
          <div class="voucher-box">
            ${voucherNumber}
          </div>
          <div class="footer">
            PhonePlay - ${formattedDate}
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(printableContent);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  }

  const renderDialog = () => {
    return (
      <Dialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        PaperProps={{
          sx: {
            direction: isRTL ? "rtl" : "ltr",
            textAlign: isRTL ? "right" : "left",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            textAlign: showOthersButtons ? "center" : isRTL ? "right" : "left",
          }}
        >
          {showOthersButtons ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '15px' }}>
                <img
                  src="https://images.celltop.vip/logo_location/nedco.jpg"
                  alt="Nedco Logo"
                  style={{ width: '80px', height: '80px', borderRadius: '10px', border: '2px solid #1976d2' }}
                />
              </div>
              <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                {translate("Voucher Generated")}
              </Typography>
            </>
          ) : (
            localeStrings["confirm_prompt"] || translate("areYouSure")
          )}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ direction: isRTL ? "rtl" : "ltr" }}>
            <Grid container spacing={2}>
              {Object.entries(userInformation || {}).map(([key, val], i) => {
                return (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    key={i}
                  >
                    <Typography
                      color="black"
                      sx={{ fontSize: '0.95rem', fontWeight: 500, textAlign: isRTL ? 'right' : 'left' }}
                    >
                      <strong>{key}:</strong> {val}
                    </Typography>
                  </Grid>
                );
              })}
              {printedData["voucher_number"] && (
                <Grid item xs={12}>
                  <div style={{ padding: "15px", border: "2px solid #1976d2", borderRadius: "8px", backgroundColor: "#f5f5f5" }}>
                    <Typography
                      color="black"
                      sx={{ fontWeight: 600, fontSize: '1.1rem', textAlign: isRTL ? 'right' : 'left' }}
                    >
                      <strong>{getLocalizedFieldLabel("voucher_number", localeStrings)}:</strong> {printedData["voucher_number"]}
                    </Typography>
                  </div>
                </Grid>
              )}
              {printedData["no_of_kw"] && (
                <Grid item xs={12}>
                  <Typography
                    color="black"
                    sx={{ fontSize: '0.95rem', fontWeight: 500, textAlign: isRTL ? 'right' : 'left' }}
                  >
                    <strong>{getLocalizedFieldLabel("no_of_kw", localeStrings)}:</strong> {printedData["no_of_kw"]}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <Button
            onClick={() => {
              setShowConfirm(false);
              setShowOthersButtons(false);
              history.goBack();
            }}
          >
            {localeStrings["Close"]}
          </Button>
          {!showOthersButtons && (
            <LoadingButton
              variant="contained"
              onClick={() => {
                if (!!nedcoBuyData) {
                  setShowConfirm(false);
                } else {
                  callBuyVoucherApi(formData);
                }
              }}
              loading={isLoading1}
              disabled={isError}
            >
              {localeStrings["Confirm"]}
            </LoadingButton>
            // <Button
            //   variant="contained"
            //   onClick={() => {
            //     if (!!nedcoBuyData) {
            //       setShowConfirm(false);
            //     } else {
            //       callBuyVoucherApi(formData);
            //     }
            //   }}
            //   autoFocus
            // >
            //   {isLoading1 ? "...تأكيد" : "تأكيد"}
            // </Button>
          )}
          {showOthersButtons && (
            <div
              style={{
                display: "flex",
                flexDirection: isRTL ? "row-reverse" : "row",
                gap: "10px",
              }}
            >
              <Button
                onClick={() => setShareModalVisible(true)}
                variant="contained"
                sx={{ height: 40, minWidth: 100 }}
              >
                {localeStrings["Share"]}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  printData(printedData);
                }}
                sx={{ height: 40, minWidth: 100 }}
              >
                {localeStrings["Print"]}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  printPOSData(printedData);
                }}
                sx={{ height: 40, minWidth: 100, backgroundColor: "#4caf50", "&:hover": { backgroundColor: "#388e3c" } }}
              >
                {localeStrings["SmallPrint"]}
              </Button>
            </div>
          )}
        </DialogActions>
      </Dialog>
    );
  };
  function enforceLTR(str) {
    return `\u202A${str}\u202C`;
  }

  return (
    <React.Fragment>
      {renderDialog()}
      <ShareModal
        shareModal={shareModalVisible}
        setShareModal={setShareModalVisible}
        contentMessage={reviewData}
        meterNumber={formData.sNumber}
      />
      <PageTitle title={translate("Nedco")} backPage="/pay_bills">
        <form
          id="nedco_form"
          dir={isRTL ? "rtl" : "ltr"}
          onSubmit={(e) => {
            e.preventDefault();
            const amount = e.target.amount.value;
            const sNumber = e.target.sNumber.value;

            if (Number(amount) < 21) {
              setAmountIsValid(false);
              return;
            }
            setAmountIsValid(true);
            setFormData({ amount, sNumber });
            nedcoEnquiry({ sNumber, amount });
          }}
        >
          <BillLogo
            src="https://images.celltop.vip/logo_location/nedco.jpg"
            alt="nedco"
            style={{}}
          />
          <Grid pt={2} container width="100%" spacing={3}>
            <Grid item xs={12} container justifyContent={"center"}>
              <Grid item xs={12} sm={10} md={8} lg={6}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <Autocomplete
                  sx={{ flex: 1 }}
                  freeSolo
                  open={autocompleteOpen}
                  onOpen={() => setAutocompleteOpen(true)}
                  onClose={() => setAutocompleteOpen(false)}
                  options={(advMode ? allCustomersList : customersList) || []}
                  loading={advMode ? loadingAllCustomers : loadingCustomers}
                  value={selectedCustomer}
                  noOptionsText={(advMode ? loadingAllCustomers : loadingCustomers) ? translate("Loading...") : translate("No customers found - type manually")}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                      setSelectedCustomer(newValue);
                      setFormData({ ...formData, sNumber: newValue });
                    } else if (newValue) {
                      setSelectedCustomer(newValue);
                      // Handle Arabic keys: "رقم العداد" = meter number
                      const meterNumber = newValue["رقم العداد"] || newValue.meter_number || "";
                      setFormData({ ...formData, sNumber: meterNumber.trim() });
                    } else {
                      setSelectedCustomer(null);
                      setFormData({ ...formData, sNumber: "" });
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    if (event) {
                      setFormData({ ...formData, sNumber: newInputValue || "" });
                    }
                  }}
                  inputValue={formData.sNumber || ""}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') {
                      return option;
                    }
                    // Handle Arabic keys from API
                    const name = (option["اسم العميل"] || option.customer_name || option.name || '').trim();
                    const meter = (option["رقم العداد"] || option.meter_number || option.meter || '').trim();

                    if (name && meter) {
                      return `${name} - ${meter}`;
                    }
                    return meter || name || '';
                  }}
                  renderOption={(props, option) => {
                    if (!option) return null;
                    // Handle Arabic keys from API
                    const name = (option["اسم العميل"] || option.customer_name || option.name || '').trim();
                    const meter = (option["رقم العداد"] || option.meter_number || option.meter || '').trim();

                    if (!name && !meter) return null;

                    return (
                      <li {...props}>
                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Typography variant="body1" fontWeight="600" color="primary">
                            {name || meter}
                          </Typography>
                          {name && meter && (
                            <Typography variant="body2" color="text.secondary">
                              {meter}
                            </Typography>
                          )}
                        </div>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      name="sNumber"
                      label={localeStrings["meter_number"]}
                      placeholder=""
                      autoFocus
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {(advMode ? loadingAllCustomers : loadingCustomers) ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      inputProps={{
                        ...params.inputProps,
                        style: {
                          ...(params.inputProps?.style || {}),
                          direction: isRTL ? 'rtl' : 'ltr',
                          textAlign: isRTL ? 'right' : 'left',
                        },
                      }}
                    />
                  )}
                />
                <Tooltip title={localeStrings["all_customers"]}>
                  <LoadingButton
                    variant={advMode ? "contained" : "outlined"}
                    color={advMode ? "success" : "primary"}
                    onClick={handleAdvClick}
                    loading={loadingAllCustomers}
                    sx={{
                      minWidth: 56,
                      height: 56,
                      px: 1,
                      flexShrink: 0,
                      ...(advMode && { boxShadow: 3, fontWeight: 700 }),
                    }}
                  >
                    {advMode ? "ES ✓" : "ES"}
                  </LoadingButton>
                </Tooltip>
                </Box>
              </Grid>
            </Grid>
            {selectedCustomer && typeof selectedCustomer === 'object' && (
              <Grid item xs={12} container justifyContent={"center"}>
                <Grid item xs={12} sm={10} md={8} lg={6}>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      p: 1.5,
                      backgroundColor: '#f0f7ff',
                      borderRadius: '8px',
                      color: '#1976d2',
                      fontWeight: 600,
                      textAlign: 'center',
                      border: '1px solid #bbdefb'
                    }}
                  >
                    {(selectedCustomer["اسم العميل"] || selectedCustomer.customer_name || '').trim()}
                  </Typography>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12} container justifyContent={"center"}>
              <Grid item xs={12} sm={10} md={8} lg={6}>
                <TextField
                  required
                  fullWidth
                  inputProps={{
                    min: 21,
                    style: {
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left',
                    },
                  }}
                  error={!amountIsValid}
                  helperText={
                    !amountIsValid && localeStrings["less_than_21"]
                  }
                  FormHelperTextProps={{
                    sx: { textAlign: isRTL ? 'right' : 'left' },
                  }}
                  name="amount"
                  placeholder={localeStrings["desired_amount"]}
                  label={localeStrings["value"]}
                />
              </Grid>
            </Grid>
            <Grid item container justifyContent={"center"}>
              <LoadingButton
                variant="contained"
                type="submit"
                loading={isLoading}
              >
                {localeStrings["search"]}
              </LoadingButton>
              {/* <Button disabled={isLoading} variant="contained" type="submit">
                {isLoading
                  ? localeStrings["searching..."]
                  : localeStrings["search"]}
              </Button> */}
            </Grid>
          </Grid>
        </form>

        <Box sx={{ display: 'flex', marginTop: 4, marginBottom: 2 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/pay_bills")}
            sx={{ minWidth: 120 }}
          >
            {localeStrings["Back"]}
          </Button>
        </Box>
      </PageTitle>
    </React.Fragment>
  );
}

Nedco.propTypes = propTypes;
